import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import RichText from './RichText';

/**
 * The guided practice run, coaching somebody through the REAL intake form.
 *
 * NOT A MODAL, AND NOT A COPY OF THE FORM. Two earlier designs were windows sitting on top of an
 * app that was right there: a dialog of prose with example answers, then a dialog containing a
 * rendered copy of the pages. This is a panel beside the real form — the person types into the
 * real fields, clicks the real Next, sees the real follow-up questions appear.
 *
 * IT ANSWERS THE QUESTION THEY ACTUALLY HAVE. A tour that walks the pages gets somebody to the
 * end of the form; it does not answer the thing they stop on, which is always a specific box —
 * "does this have to add to 100?", "why has this filled itself in?". So CLICKING ANY FIELD swaps
 * the panel over to explaining that field, and a click on the background puts the tour back. That
 * is the difference between a product somebody has to be trained on and one that explains itself.
 *
 * IT GETS OUT OF THE WAY. It starts in the middle, where it will be read, and can be dragged
 * anywhere, made larger, collapsed to a bar, or closed. A guide you cannot move is a guide people
 * close and never reopen.
 *
 * IT CANNOT PERSIST ANYTHING. No Firestore, no callables, no storage — it narrates and changes
 * page. The "nothing is saved" guarantee lives in IntakeForm's `practice` flag, so it does not
 * depend on this component behaving itself. See lib/practiceMode.test.js.
 */

const OPENING = {
  id: 'practice:intro',
  title: 'This is the real form',
  body: [
    'Everything here works: type into the boxes, open the dropdowns, answer a question and watch the follow-up appear.',
    '**Click any question and this panel will explain it.** Click the background to come back to the tour.',
    '**Nothing is saved.** No draft is created, nothing is submitted, and it all disappears when you leave.',
  ],
};

/** Steps about the rest of the app, which highlight a real thing in the rail. */
const RAIL_STEPS = [
  {
    id: 'practice:my-matters',
    title: 'My matters — everything you have started',
    highlight: '[data-rail="/matters"]',
    body: [
      'Every matter you have started lives here, drafts included. A draft appears the moment you begin typing, so nothing is ever lost because you were interrupted.',
      'Open one to carry on where you stopped, to download its PDF again, or just to look up what was entered.',
    ],
  },
  {
    id: 'practice:templates',
    title: 'Templates — for the client you act for often',
    highlight: '[data-rail="/templates"]',
    body: [
      'If you open several matters for the same client, most of the form is the same every time. Fill it in once, use **Save as template** at the bottom of the form, and start from it next time.',
      '**Only you can see your own templates.** They never carry matter numbers, attachments, or anything an administrator has marked as never-copy, so last month’s paperwork cannot follow you into this month’s matter.',
    ],
  },
];

const CLOSING = {
  id: 'practice:done',
  title: 'That is the whole thing',
  body: [
    'For real, the last step checks your answers and produces the **PDF** — the full record of everything you entered, emailed to you and kept on the matter.',
    'Nothing you typed here was saved. When you want a real one, it is **New matter** in the left rail.',
  ],
};

/** A ring drawn over a real element, so "this one" means something. */
function Spotlight({ selector }) {
  const [rect, setRect] = useState(null);

  useEffect(() => {
    if (!selector) {
      setRect(null);
      return undefined;
    }
    // Followed rather than measured once: the rail is fixed, but the form scrolls and reflows as
    // conditional questions appear, and a ring left behind at a stale position is worse than no
    // ring — it points confidently at the wrong thing.
    const measure = () => {
      const node = document.querySelector(selector);
      setRect(node ? node.getBoundingClientRect() : null);
    };
    measure();
    const timer = setInterval(measure, 250);
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    return () => {
      clearInterval(timer);
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [selector]);

  if (!rect || (!rect.width && !rect.height)) return null;
  return (
    <div
      aria-hidden="true"
      className="pointer-events-none fixed z-[45] rounded-lg ring-4 ring-accent-500 ring-offset-2 ring-offset-transparent transition-all duration-150"
      style={{ top: rect.top - 4, left: rect.left - 4, width: rect.width + 8, height: rect.height + 8 }}
    />
  );
}

export default function PracticeCoach({
  sections,
  activeSection,
  onGoToSection,
  guidanceFor,
  fieldGuidance,
  onClearField,
  onExit,
}) {
  const [collapsed, setCollapsed] = useState(false);
  const [large, setLarge] = useState(false);
  const [phase, setPhase] = useState('intro'); // intro -> pages -> rail -> done
  const [railIndex, setRailIndex] = useState(0);

  // Centred to start with, because a panel in the corner is a panel nobody reads. `null` means
  // "not placed yet" and is resolved on mount, once the size is known.
  const [pos, setPos] = useState(null);
  const panelRef = useRef(null);
  const drag = useRef(null);

  useEffect(() => {
    if (pos || !panelRef.current) return;
    const box = panelRef.current.getBoundingClientRect();
    setPos({
      x: Math.max(12, (window.innerWidth - box.width) / 2),
      y: Math.max(12, (window.innerHeight - box.height) / 2),
    });
  }, [pos]);

  const onPointerDown = (event) => {
    if (!pos) return;
    drag.current = { dx: event.clientX - pos.x, dy: event.clientY - pos.y };
    event.currentTarget.setPointerCapture(event.pointerId);
  };
  const onPointerMove = (event) => {
    if (!drag.current) return;
    const box = panelRef.current?.getBoundingClientRect();
    const width = box?.width || 360;
    const height = box?.height || 240;
    // Clamped so it cannot be dragged off-screen and lost — leaving a tour stranded outside the
    // viewport with no way back is the one drag bug worth preventing outright.
    setPos({
      x: Math.min(Math.max(8, event.clientX - drag.current.dx), window.innerWidth - width - 8),
      y: Math.min(Math.max(8, event.clientY - drag.current.dy), window.innerHeight - height - 8),
    });
  };
  const endDrag = () => {
    drag.current = null;
  };

  const pageSteps = useMemo(
    () =>
      (sections || []).map((section) => ({
        id: `practice:page:${section.id}`,
        title: section.title || 'Untitled page',
        body: guidanceFor ? guidanceFor(section) : [],
      })),
    [sections, guidanceFor]
  );

  // Leaving the opening hands control to the form; reaching any page past the first does the same,
  // so somebody who ignores the panel and clicks straight into the form is not left behind by it.
  useEffect(() => {
    if (activeSection > 0 && phase === 'intro') setPhase('pages');
  }, [activeSection, phase]);

  const step =
    phase === 'intro'
      ? OPENING
      : phase === 'rail'
        ? RAIL_STEPS[railIndex]
        : phase === 'done'
          ? CLOSING
          : pageSteps[Math.min(activeSection, pageSteps.length - 1)] || OPENING;

  const next = useCallback(() => {
    if (phase === 'intro') return setPhase('pages');
    if (phase === 'pages') {
      if (activeSection < sections.length - 1) return onGoToSection(activeSection + 1);
      return setPhase('rail');
    }
    if (phase === 'rail') {
      if (railIndex < RAIL_STEPS.length - 1) return setRailIndex(railIndex + 1);
      return setPhase('done');
    }
    return onExit();
  }, [phase, activeSection, sections, onGoToSection, railIndex, onExit]);

  const back = useCallback(() => {
    if (phase === 'done') return setPhase('rail');
    if (phase === 'rail') {
      if (railIndex > 0) return setRailIndex(railIndex - 1);
      return setPhase('pages');
    }
    if (phase === 'pages') {
      if (activeSection > 0) return onGoToSection(activeSection - 1);
      return setPhase('intro');
    }
    return undefined;
  }, [phase, railIndex, activeSection, onGoToSection]);

  // A field explanation takes the panel over while it is showing; the tour keeps its place behind.
  const showingField = Boolean(fieldGuidance);
  const title = showingField ? fieldGuidance.title : step.title;
  const body = showingField ? fieldGuidance.body : step.body;
  const highlight = showingField ? null : step.highlight;

  if (collapsed) {
    return (
      <div className="fixed bottom-0 left-0 right-0 z-[46] flex items-center gap-3 bg-firm-700 px-4 py-2 text-sm text-white dark:bg-firm-800">
        <span className="font-medium">Practice run</span>
        <span className="text-firm-200">nothing here is saved</span>
        <button type="button" onClick={() => setCollapsed(false)} className="ml-auto text-xs underline">
          Show the guide
        </button>
        <button type="button" onClick={onExit} className="text-xs underline">
          Exit
        </button>
      </div>
    );
  }

  return (
    <>
      <Spotlight selector={highlight} />
      <aside
        ref={panelRef}
        aria-label="Practice run guide"
        className={`fixed z-[46] overflow-hidden rounded-xl border border-slate-200 bg-white shadow-2xl dark:border-slate-700 dark:bg-slate-900 ${
          large ? 'w-[34rem]' : 'w-[24rem]'
        } max-w-[calc(100vw-1.5rem)]`}
        style={pos ? { top: pos.y, left: pos.x } : { top: '35vh', left: '50%', transform: 'translateX(-50%)' }}
      >
        <div
          className="flex cursor-grab items-start justify-between gap-2 bg-firm-700 px-4 py-2.5 active:cursor-grabbing dark:bg-firm-800"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
        >
          <div className="min-w-0 select-none">
            <p className="text-[11px] uppercase tracking-wide text-firm-200">
              {showingField ? 'About this question' : 'Practice run · nothing is saved'}
            </p>
            <h2 className="truncate text-sm font-semibold text-white">{title}</h2>
          </div>
          <div className="flex shrink-0 items-center gap-1">
            <button
              type="button"
              onClick={() => setLarge((v) => !v)}
              className="rounded px-1.5 py-0.5 text-xs text-firm-200 hover:bg-white/10 hover:text-white"
              aria-label={large ? 'Make the guide smaller' : 'Make the guide bigger'}
            >
              {large ? '⤡' : '⤢'}
            </button>
            <button
              type="button"
              onClick={() => setCollapsed(true)}
              className="rounded px-1.5 py-0.5 text-xs text-firm-200 hover:bg-white/10 hover:text-white"
              aria-label="Collapse the guide"
            >
              —
            </button>
          </div>
        </div>

        <div className={`overflow-y-auto px-4 py-3 ${large ? 'max-h-[55vh]' : 'max-h-[42vh]'}`}>
          {body.length ? (
            body.map((paragraph, i) => (
              <p
                key={i}
                className={`mt-2 leading-relaxed text-slate-700 first:mt-0 dark:text-slate-200 ${
                  large ? 'text-sm' : 'text-[13px]'
                }`}
              >
                <RichText>{paragraph}</RichText>
              </p>
            ))
          ) : (
            <p className="text-[13px] leading-relaxed text-slate-600 dark:text-slate-300">
              Have a look at this page and fill in whatever you like. Click any question for an
              explanation of it.
            </p>
          )}
        </div>

        <div className="flex items-center gap-2 border-t border-slate-200 px-4 py-2.5 dark:border-slate-700">
          {showingField ? (
            <>
              <span className="text-[11px] text-slate-500 dark:text-slate-400">
                Click another question, or go back to the tour.
              </span>
              <button
                type="button"
                onClick={onClearField}
                className="ml-auto rounded bg-firm-700 px-3 py-1 text-xs font-medium text-white hover:bg-firm-900 dark:bg-firm-500 dark:hover:bg-firm-400"
              >
                Back to the tour
              </button>
            </>
          ) : (
            <>
              <span className="text-[11px] text-slate-500 dark:text-slate-400">
                {phase === 'pages' ? `Page ${activeSection + 1} of ${sections.length}` : ''}
              </span>
              <div className="ml-auto flex items-center gap-2">
                <button
                  type="button"
                  onClick={onExit}
                  className="text-xs text-slate-500 underline dark:text-slate-400"
                >
                  Exit
                </button>
                {phase !== 'intro' && (
                  <button
                    type="button"
                    onClick={back}
                    className="rounded border border-slate-300 px-2.5 py-1 text-xs text-slate-700 dark:border-slate-600 dark:text-slate-200"
                  >
                    Back
                  </button>
                )}
                <button
                  type="button"
                  onClick={next}
                  className="rounded bg-firm-700 px-3 py-1 text-xs font-medium text-white hover:bg-firm-900 dark:bg-firm-500 dark:hover:bg-firm-400"
                >
                  {phase === 'done' ? 'Finish' : 'Next'}
                </button>
              </div>
            </>
          )}
        </div>
      </aside>
    </>
  );
}
